import React, { useState } from 'react';
import type { Player } from '../types/game';

interface HostSetupScreenProps {
  gameId: string;
  gameCode: string;
  players: Player[];
  onAddPlayer: (name: string) => void;
  onRemovePlayer: (id: string) => void;
  onStartGame: () => void;
  onBack: () => void;
}

const HostSetupScreen: React.FC<HostSetupScreenProps> = ({
  gameId,
  gameCode,
  players,
  onAddPlayer,
  onRemovePlayer,
  onStartGame,
  onBack,
}) => {
  const [playerName, setPlayerName] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(gameCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleAdd = () => {
    if (playerName.trim()) {
      onAddPlayer(playerName.trim());
      setPlayerName('');
    }
  };

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-16" />
          <div className="flex items-center gap-8">
            <div className="text-right">
              <p className="text-brand-yellow text-xl font-bold">Game Code</p>
              <p className="text-white text-2xl tracking-widest">{gameCode}</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-black rounded-2xl p-10 shadow-2xl border-2 border-brand-yellow/30">
          <h2 className="text-3xl font-extrabold mb-10 text-brand-yellow flex items-center gap-3 tracking-wide">
            <img src="/images/white-glasses.svg" alt="Host Setup" className="h-7" />
            Host Setup
          </h2>

          {/* Game Code Section */}
          <div className="mb-10">
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <div className="w-full text-3xl font-mono font-extrabold bg-black text-brand-yellow border-2 border-brand-yellow rounded-xl py-4 px-6 text-center select-all tracking-widest shadow-md">
                  {gameCode}
                </div>
              </div>
              <button
                className="bg-brand-yellow text-black font-bold px-7 py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors text-lg shadow-md"
                onClick={handleCopy}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Player Management */}
          <div className="mb-10">
            <div className="flex gap-4 mb-7">
              <input
                className="flex-1 bg-black text-white rounded-xl px-5 py-4 border-2 border-brand-yellow/40 focus:border-brand-yellow focus:outline-none text-lg shadow-inner"
                placeholder="Enter player name"
                value={playerName}
                onChange={e => setPlayerName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <button
                className="bg-brand-yellow text-black font-bold px-7 py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors text-lg shadow-md disabled:opacity-50"
                onClick={handleAdd}
                disabled={!playerName.trim()}
              >
                Add Player
              </button>
            </div>

            <div className="bg-[#181818] rounded-2xl p-7 border-2 border-brand-yellow/20 shadow-lg">
              <h3 className="text-2xl font-extrabold mb-6 text-brand-yellow flex items-center gap-2 tracking-wide">
                <img src="/images/white-glasses.svg" alt="Players" className="h-6" />
                Players ({players.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {players.map(player => (
                  <div key={player.id} className="flex items-center gap-5 bg-black rounded-xl px-7 py-5 border-2 border-brand-yellow/30 shadow-md">
                    <div className="h-14 w-14 rounded-full bg-[#222] flex items-center justify-center border-2 border-brand-yellow">
                      <img src={`/images/${player.avatar}`} alt="Avatar" className="h-10 w-10" />
                    </div>
                    <div className="flex-1">
                      <p className="font-extrabold text-brand-yellow text-xl tracking-wide">{player.name}</p>
                    </div>
                    <button
                      className="text-red-400 hover:text-red-300 font-bold px-3 py-1 rounded transition-colors border border-transparent hover:border-red-400"
                      onClick={() => onRemovePlayer(player.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-5 mt-8">
            <button
              className="flex-1 bg-brand-yellow text-black font-extrabold px-10 py-5 rounded-xl hover:bg-[#fccd6c]/90 transition-colors text-2xl shadow-lg disabled:opacity-50"
              onClick={onStartGame}
              disabled={players.length === 0}
            >
              Start Game
            </button>
            <button
              className="bg-[#222] text-white font-bold px-10 py-5 rounded-xl hover:bg-[#444]/90 transition-colors text-2xl border-2 border-brand-yellow/30 shadow-lg"
              onClick={onBack}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostSetupScreen; 